import { useState, FormEvent, useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { personalInfo } from "@/mocks/devData";

gsap.registerPlugin(ScrollTrigger);

// Replace with your Web3Forms access key: https://web3forms.com/
const WEB3FORMS_ACCESS_KEY = "db2add41-cfc4-4d71-baae-cf19fcf09463";

export default function DevContact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add(
        {
          isMobile: "(max-width: 767px)",
          isDesktop: "(min-width: 768px)",
        },
        (context) => {
          const { isMobile } = context.conditions as { isMobile: boolean };

          gsap.fromTo(
            headerRef.current,
            isMobile
              ? { opacity: 0, y: 24 }
              : { opacity: 0, y: 60, rotateX: -20, transformPerspective: 1000 },
            {
              opacity: 1,
              y: 0,
              rotateX: 0,
              duration: isMobile ? 0.6 : 1,
              ease: "power3.out",
              scrollTrigger: { trigger: headerRef.current, start: "top 90%", once: true },
            }
          );

          gsap.fromTo(
            leftRef.current,
            isMobile ? { opacity: 0, y: 24 } : { opacity: 0, x: -70, rotateY: 20, transformPerspective: 1000 },
            {
              opacity: 1,
              x: 0,
              y: 0,
              rotateY: 0,
              duration: isMobile ? 0.6 : 0.9,
              ease: "power3.out",
              scrollTrigger: { trigger: leftRef.current, start: "top 90%", once: true },
            }
          );

          gsap.fromTo(
            rightRef.current,
            isMobile ? { opacity: 0, y: 24 } : { opacity: 0, x: 70, rotateY: -20, transformPerspective: 1000 },
            {
              opacity: 1,
              x: 0,
              y: 0,
              rotateY: 0,
              duration: isMobile ? 0.6 : 0.9,
              ease: "power3.out",
              scrollTrigger: { trigger: rightRef.current, start: "top 90%", once: true },
            }
          );
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const textarea = messageRef.current;
    if (textarea && textarea.value.length > 500) return;

    setLoading(true);
    setError(false);
    setErrorMsg("");

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          name: nameRef.current?.value || "",
          email: emailRef.current?.value || "",
          subject: subjectRef.current?.value
            ? `Portfolio: ${subjectRef.current.value}`
            : `New portfolio message from ${nameRef.current?.value || "a visitor"}`,
          message: messageRef.current?.value || "",
          botcheck: "",
        }),
      });
      const data = await res.json();
      console.log("Web3Forms response:", data);

      if (data.success) {
        setSubmitted(true);
      } else {
        setError(true);
        setErrorMsg(data.message || "Unknown error from Web3Forms");
      }
    } catch (err) {
      console.error("Contact form submit failed:", err);
      setError(true);
      setErrorMsg(err instanceof Error ? err.message : "Network error");
    } finally {
      setLoading(false);
    }
  };

  const contactItems = [
    { icon: "ri-mail-line", label: "Email", value: personalInfo.email, href: `mailto:${personalInfo.email}` },
    { icon: "ri-github-fill", label: "GitHub", value: "github.com/hr-safin", href: personalInfo.github },
    { icon: "ri-linkedin-box-fill", label: "LinkedIn", value: "hasibur-rahman-safin", href: personalInfo.linkedin },
    { icon: "ri-map-pin-2-fill", label: "Location", value: personalInfo.location, href: "" },
  ];

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative w-full py-16 sm:py-20 md:py-28 overflow-x-hidden overflow-y-visible"
      style={{
        background: `
          radial-gradient(at 20% 20%, rgba(22,163,74,0.05) 0px, transparent 50%),
          radial-gradient(at 80% 80%, rgba(22,163,74,0.04) 0px, transparent 50%),
          #000000
        `,
        maxWidth: "100vw",
      }}
    >
      {/* Extra clipping wrapper so blurred orbs can never widen the section's scrollable box */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="orb-1 absolute top-1/4 left-1/4 w-[220px] h-[220px] sm:w-[500px] sm:h-[500px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(22,163,74,0.05) 0%, transparent 65%)", filter: "blur(60px)" }}
        />
        <div
          className="orb-2 absolute bottom-1/4 right-1/4 w-[180px] h-[180px] sm:w-[350px] sm:h-[350px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(22,163,74,0.035) 0%, transparent 65%)", filter: "blur(60px)" }}
        />
      </div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-4 xs:px-5 sm:px-6 lg:px-8 box-border">
        <div ref={headerRef} className="mb-10 sm:mb-14 w-full">
          <p className="text-xs font-mono text-gray-600 tracking-widest uppercase mb-3">Let&apos;s Talk</p>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-4">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-light text-white tracking-tight break-words"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Let&apos;s <span className="font-black text-green-600">Connect</span>
            </h2>
            <p className="text-gray-500 text-sm max-w-full sm:max-w-xs leading-relaxed">
              Have a project in mind? I&apos;d love to hear about it.
            </p>
          </div>
          <div className="mt-6 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />
        </div>

        <div
          className="w-full rounded-2xl sm:rounded-3xl overflow-hidden box-border"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)" }}
        >
          <div className="grid lg:grid-cols-2 w-full">
            {/* Left */}
            <div
              ref={leftRef}
              className="min-w-0 w-full p-5 sm:p-7 md:p-10 border-b lg:border-b-0 lg:border-r box-border"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
            >
              <p className="text-gray-400 text-sm leading-relaxed mb-6 sm:mb-7">
                I welcome opportunities for collaboration, partnership, and meaningful discussions in technology and
                development.
              </p>

              <div className="flex flex-col w-full min-w-0">
                {contactItems.map((item, idx) => {
                  const content = (
                    <>
                      <div
                        className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-all duration-200 group-hover:scale-105"
                        style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.15)" }}
                      >
                        <i className={`${item.icon} text-green-400 text-sm sm:text-base`} />
                      </div>
                      <div className="min-w-0 flex-1 overflow-hidden">
                        <div className="text-gray-500 text-xs font-mono">{item.label}</div>
                        <div className="text-gray-100 text-sm font-medium truncate">{item.value}</div>
                      </div>
                      {item.href && (
                        <i className="ri-arrow-right-up-line text-gray-500 group-hover:text-green-400 flex-shrink-0 transition-colors duration-200 text-sm" />
                      )}
                    </>
                  );

                  const rowClass = `group flex items-center gap-3 sm:gap-4 py-3 sm:py-3.5 w-full min-w-0 transition-all duration-200 ${
                    idx > 0 ? "border-t" : ""
                  }`;
                  const rowStyle = { borderColor: "rgba(255,255,255,0.08)" };

                  return item.href ? (
                    <a
                      key={item.label}
                      href={item.href}
                      target={!item.href.startsWith("mailto") ? "_blank" : undefined}
                      rel="noopener noreferrer"
                      className={`${rowClass} cursor-pointer`}
                      style={rowStyle}
                    >
                      {content}
                    </a>
                  ) : (
                    <div key={item.label} className={rowClass} style={rowStyle}>
                      {content}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Form */}
            <div ref={rightRef} className="min-w-0 w-full p-5 sm:p-7 md:p-10 box-border">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-10 sm:py-12 gap-4">
                  <div
                    className="w-14 h-14 flex items-center justify-center rounded-full"
                    style={{ background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.25)" }}
                  >
                    <i className="ri-mail-check-line text-green-400 text-2xl" />
                  </div>
                  <h3 className="text-white text-lg font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    Message Sent!
                  </h3>
                  <p className="text-gray-400 text-sm text-center">
                    Thanks for reaching out — I&apos;ll get back to you soon.
                  </p>
                </div>
              ) : (
                <form id="dev-contact-form" onSubmit={handleSubmit} className="flex flex-col gap-4 w-full min-w-0">
                  <div className="flex flex-col sm:flex-row gap-4 w-full min-w-0">
                    <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                      <label className="text-gray-400 text-xs font-mono">Full Name</label>
                      <input
                        ref={nameRef}
                        name="name"
                        type="text"
                        required
                        placeholder="Elon Musk"
                        className="w-full min-w-0 box-border rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-green-500/40 transition-all"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                      <label className="text-gray-400 text-xs font-mono">Email</label>
                      <input
                        ref={emailRef}
                        name="email"
                        type="email"
                        required
                        placeholder="xyz@example.com"
                        className="w-full min-w-0 box-border rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-green-500/40 transition-all"
                        style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5 w-full min-w-0">
                    <label className="text-gray-400 text-xs font-mono">Subject</label>
                    <input
                      ref={subjectRef}
                      name="subject"
                      type="text"
                      required
                      placeholder="Project inquiry"
                      className="w-full min-w-0 box-border rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-green-500/40 transition-all"
                      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5 w-full min-w-0">
                    <label className="text-gray-400 text-xs font-mono">
                      Message <span className="text-gray-500">(max 500 chars)</span>
                    </label>
                    <textarea
                      ref={messageRef}
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Tell me about your project..."
                      className="w-full min-w-0 box-border rounded-xl px-4 py-2.5 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-green-500/40 transition-all resize-none"
                      style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.1)" }}
                    />
                  </div>

                  {/* Honeypot */}
                  <input type="checkbox" name="botcheck" tabIndex={-1} autoComplete="off" style={{ display: "none" }} />

                  {error && (
                    <p className="text-red-400 text-xs font-mono text-center break-words">
                      {errorMsg || "Something went wrong."} Please try again or email me directly.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-3d btn-3d-primary w-full inline-flex items-center justify-center gap-2 py-2.5 text-xs sm:py-3 sm:text-sm"
                  >
                    {loading ? (
                      "Sending..."
                    ) : (
                      <>
                        <i className="ri-send-plane-line" /> Send Message
                      </>
                    )}
                  </button>
                  <p className="text-center text-gray-500 text-xs font-mono">I typically reply within 24 hours</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}